// map-animation.service.ts
import { inject, Injectable } from '@angular/core';
import { GeoJSONSource } from 'maplibre-gl';
import * as turf from '@turf/turf';
import { MapStateService } from './map-state.service';
import { AnimationStateService } from './animation-state.service';
import { ColorInterpolationService } from './color-interpolation.service';
import { Position } from '@wheere/api-tracker';

@Injectable({
  providedIn: 'root',
})
export class MapAnimationService {
  private mapStateService = inject(MapStateService);
  private animationStateService = inject(AnimationStateService);
  private colorInterpolationService = inject(ColorInterpolationService);

  private keepLinesBehind = true;
  private animationsStep = 2;
  private segments: {
    arc: any[];
    sourceId: string;
    layerId: string;
    point: GeoJSON.FeatureCollection;
    startColor: string;
    endColor: string;
  }[] = [];

  public startAnimation(positions: Position[], animationsStep: number, keepLinesBehind: boolean) {
    this.animationsStep = animationsStep;
    this.keepLinesBehind = keepLinesBehind;

    const animationState = this.animationStateService.animationState();

    if (animationState === 'paused') {
      // Resume the animation
      this.animationStateService.animationState.set('playing');
      this.animateSegment(this.animationStateService.currentSegmentIndex);
    } else {
      // Start a new animation
      this.mapStateService.removeAllLayers(['route-', 'point-']);

      // Reset variables
      this.animationStateService.currentSegmentIndex = 0;
      this.animationStateService.currentCounter = 0;
      this.segments = [];

      // Set the animation state to playing
      this.animationStateService.animationState.set('playing');

      const coordinates = positions.map(pos => [pos.longitude!, pos.latitude!]);

      for (let i = 0; i < coordinates.length - 1; i++) {
        const segment = turf.lineString([coordinates[i], coordinates[i + 1]]);
        const segmentDistance = turf.length(segment, { units: 'kilometers' });

        const startColor = '#1a36e8';
        const endColor = '#fff000';

        const arc: any[] = [];
        const steps = this.animationsStep * 100; // Steps per segment
        for (let j = 0; j <= steps; j++) {
          const interpolated = turf.along(segment, (segmentDistance / steps) * j, { units: 'kilometers' });
          arc.push(interpolated.geometry.coordinates);
        }

        const sourceId = `route-segment-${i}`;
        const layerId = `point-segment-${i}`;

        // Store each segment's data
        const point: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: { bearing: 0 },
              geometry: {
                type: 'Point',
                coordinates: arc[0], // Start of the arc
              },
            },
          ],
        };

        this.segments.push({ arc, sourceId, layerId, point, startColor, endColor });
      }

      // Start animation with the first segment
      this.animateSegment(this.animationStateService.currentSegmentIndex);
    }
  }

  public pauseAnimation() {
    if (this.animationStateService.animationState() === 'playing') {
      this.animationStateService.animationState.set('paused');
    }
  }

  public stopAnimation() {
    this.animationStateService.animationState.set('stopped');
    this.mapStateService.removeAllLayers(['route-', 'point-']);
    // Reset variables
    this.animationStateService.currentSegmentIndex = 0;
    this.animationStateService.currentCounter = 0;
    this.segments = [];
  }

  private animateSegment(index: number) {
    const map = this.mapStateService.map();
    if (!map) return;

    const animationState = this.animationStateService.animationState();

    if (animationState !== 'playing') {
      // If animation is paused or stopped, exit
      return;
    }

    if (index >= this.segments.length) {
      console.log('All segments animated.');
      this.animationStateService.animationState.set('stopped'); // Set animation state to stopped when done
      return;
    }

    const { arc, sourceId, layerId, point, startColor, endColor } = this.segments[index];

    // If starting a new segment, add layers and reset counter
    if (this.animationStateService.currentCounter === 0) {
      // Add the route and point layers for the current segment
      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: arc,
              },
            },
          ],
        },
      });

      map.addLayer({
        id: sourceId,
        source: sourceId,
        type: 'line',
        paint: {
          'line-width': 2,
          'line-color': startColor,
        },
      });

      map.addSource(layerId, {
        type: 'geojson',
        data: point,
      });

      map.addLayer({
        id: layerId,
        source: layerId,
        type: 'circle',
        paint: {
          'circle-radius': 6,
          'circle-color': startColor,
        },
      });
    }

    const animate = () => {
      const animationState = this.animationStateService.animationState();

      if (animationState === 'paused') {
        // If animation is paused, exit but keep current state
        return;
      }

      if (animationState !== 'playing') {
        // If animation is stopped, remove layers and exit
        this.mapStateService.removeLayers([sourceId, layerId]);
        return;
      }

      if (this.animationStateService.currentCounter < arc.length) {
        const progress = this.animationStateService.currentCounter / (arc.length - 1);
        const currentColor = this.colorInterpolationService.interpolateColor(startColor, endColor, progress);

        // Update point position
        if ('coordinates' in point.features[0].geometry) {
          point.features[0].geometry.coordinates = arc[this.animationStateService.currentCounter];
        }

        const currentCoord = arc[this.animationStateService.currentCounter];
        const nextCoord = arc[Math.min(this.animationStateService.currentCounter + 1, arc.length - 1)];

        if (currentCoord && nextCoord && point.features[0].properties && 'bearing' in point.features[0].properties) {
          point.features[0].properties['bearing'] = turf.bearing(turf.point(currentCoord), turf.point(nextCoord));
        }

        // Update the point source
        const pointSource = map.getSource(layerId) as GeoJSONSource;
        pointSource.setData(point);

        map.setPaintProperty(layerId, 'circle-color', currentColor);

        // Update the line's coordinates and color
        const partialArc = arc.slice(0, this.animationStateService.currentCounter + 1);
        const lineSource = map.getSource(sourceId) as GeoJSONSource;
        lineSource.setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: partialArc,
          },
        } as any);

        map.setPaintProperty(sourceId, 'line-color', currentColor);
        // Increment counter
        this.animationStateService.currentCounter++;
        requestAnimationFrame(animate);
      } else {
        if (!this.keepLinesBehind) {
          this.mapStateService.removeLayers([sourceId]);
        }
        this.mapStateService.removeLayers([layerId]);

        // Move to the next segment
        this.animationStateService.currentSegmentIndex++;
        this.animationStateService.currentCounter = 0;
        this.animateSegment(this.animationStateService.currentSegmentIndex);
      }
    };

    animate();
  }
}
